import axios, { type AxiosAdapter, type AxiosRequestConfig } from 'axios';

/** Runtime accepts `(name, config)`; axios 1.18 typings only declare the name. */
const resolveFetchAdapter = (config: AxiosRequestConfig): AxiosAdapter =>
  (
    axios.getAdapter as (
      name: 'fetch',
      cfg: AxiosRequestConfig,
    ) => AxiosAdapter
  )('fetch', config);

const stripUserAgentBeforeFetch = (nativeFetch: typeof fetch): typeof fetch => {
  return (input, init?) => {
    if (typeof Request !== 'undefined' && input instanceof Request) {
      if (
        !input.headers.has('User-Agent') &&
        !input.headers.has('user-agent')
      ) {
        return nativeFetch(input, init);
      }

      const headers = new Headers(input.headers);
      headers.delete('User-Agent');
      return nativeFetch(new Request(input, { headers }), init);
    }

    if (!init?.headers) {
      return nativeFetch(input, init);
    }

    const headers = new Headers(init.headers as HeadersInit);
    if (!headers.has('User-Agent') && !headers.has('user-agent')) {
      return nativeFetch(input, init);
    }

    headers.delete('User-Agent');
    return nativeFetch(input, { ...init, headers });
  };
};

/** One stripping wrapper per native `fetch` — axios caches adapters by `env.fetch` identity. */
const strippingFetchByNativeFetch = new WeakMap<typeof fetch, typeof fetch>();

const getStrippingFetch = (nativeFetch: typeof fetch): typeof fetch => {
  let strippingFetch = strippingFetchByNativeFetch.get(nativeFetch);
  if (!strippingFetch) {
    strippingFetch = stripUserAgentBeforeFetch(nativeFetch);
    strippingFetchByNativeFetch.set(nativeFetch, strippingFetch);
  }
  return strippingFetch;
};

let defaultNativeFetch: typeof fetch | undefined;

const getDefaultNativeFetch = (): typeof fetch => {
  defaultNativeFetch ??= globalThis.fetch.bind(globalThis);
  return defaultNativeFetch;
};

/**
 * Axios fetch adapter for Fabric API calls: keeps {@link createApiAxios}'s
 * `credentials: 'omit'` behaviour and removes axios's default `User-Agent`
 * before `fetch()`. iOS WebKit (Safari, Chrome on iOS, etc.) treats a
 * script-set User-Agent as CORS-unsafe on cross-origin requests; preflight
 * then requires `User-Agent` in `Access-Control-Allow-Headers`, which Fabric
 * does not expose.
 *
 * Resolves the fetch adapter via {@link axios.getAdapter} with `config.env.fetch`
 * set to the stripping wrapper. Axios binds `fetch` at adapter factory time, not
 * per request — reusing a cached default fetch adapter would ignore that env.
 */
export const omitCredentialsFetchAdapter: AxiosAdapter = (config) => {
  const isBrowser =
    typeof globalThis.window !== 'undefined' &&
    typeof globalThis.fetch === 'function';

  if (!isBrowser) {
    return resolveFetchAdapter(config)(config);
  }

  const nativeFetch = (config.env?.fetch ?? getDefaultNativeFetch()) as typeof fetch;

  const fetchEnvConfig = {
    ...config,
    env: {
      ...config.env,
      fetch: getStrippingFetch(nativeFetch),
      Request: config.env?.Request ?? globalThis.Request,
      Response: config.env?.Response ?? (globalThis.Response as never),
    },
  };

  return resolveFetchAdapter(fetchEnvConfig)(config);
};
